import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'

import dayjs from '../../../utils/dayjsConfig'

import * as Yup from 'yup'
import { useFormik } from 'formik'

import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import { addCompanyPeriod } from '../../../services/CompaniesService'
import { computeBackendDayLimit } from '../../../utils/quincena'
import { useApiErrorMessage } from '../../../i18n/useApiErrorMessage'

const periodsBefore = [
  {
    id: 1,
    value: '1',
  },
  {
    id: 2,
    value: '2',
  },
  {
    id: 3,
    value: '3',
  },
  {
    id: 4,
    value: '4',
  },
  {
    id: 5,
    value: '5',
  },
]

type PeriodoStepperDetailsType = {
  handleClose: (status: boolean) => void
  handleReset: () => void
  periodo: any
  handleRefreshTable: (status: boolean) => void
  setDisabledOnSubmit: React.Dispatch<React.SetStateAction<boolean>>
  setOpenErrorModal: React.Dispatch<React.SetStateAction<boolean>>
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
}

const PeriodoStepperDetails: React.FC<PeriodoStepperDetailsType> = ({
  handleClose,
  handleRefreshTable,
  handleReset,
  periodo,
  setDisabledOnSubmit,
  setErrorMessage,
  setOpenErrorModal,
}) => {
  const { t } = useTranslation('agregarPeriodoModal')
  const getApiErrorMessage = useApiErrorMessage()
  const { getAccessTokenSilently } = useAuth0()
  const params = useParams()
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)

  const now = dayjs().tz('America/Argentina/Buenos_Aires')
  const todayIsFirstHalf = now.date() <= 15

  const durationArray = [
    {
      id: 0,
      value: t('duration.monthly'),
    },
    {
      id: 1,
      value: t('duration.biweekly'),
    },
  ]

  const formik = useFormik({
    initialValues: {
      name: '',
      duration: 0,
      day_limit: '',
      periods_before: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().max(55).required(t('validation.nameRequired')),
      duration: Yup.number().max(365).required(t('validation.durationRequired')),
      periods_before: Yup.number().max(365).required(t('validation.advanceRequired')),
      day_limit: Yup.string()
        .test(
          'is-date',
          t('validation.invalidDate'),
          value => !!value && dayjs(value).isValid(),
        )
        .required(t('validation.dayLimitDateRequired')),
    }),
    onSubmit: async data => {
      setDisabledOnSubmit(true)
      let computedDayLimit: number | null = null
      if (data.day_limit) {
        computedDayLimit = computeBackendDayLimit(Number(data.duration), data.day_limit)
      }
      const finalData = {
        ...data,
        day_limit: computedDayLimit,
        periods_before: Number(data.periods_before),
        company_id: params.empresaId,
        reference_date: data.day_limit,
      }
      const token = await getAccessTokenSilently()
      addCompanyPeriod(token, finalData)
        .then(result => {
          if (result.status === 201 || result.status === 200) {
            handleReset()
            handleRefreshTable(true)
            handleClose(false)
            setDisabledOnSubmit(false)
          }
        })
        .catch(e => {
          console.error('error', e.response.data.error)
          setErrorMessage(getApiErrorMessage(e))
          setDisabledOnSubmit(false)
          setOpenErrorModal(true)
        })
    },
  })

  useEffect(() => {
    if (periodo) {
      // If the period type has a reference_date, use it as the day_limit date
      // Otherwise try to parse from name (DD/MM/YYYY format)
      let dayLimitDate = ''
      if (periodo.reference_date && periodo.reference_date !== '0001-01-01T00:00:00Z') {
        dayLimitDate = dayjs(periodo.reference_date).toISOString()
      } else if (periodo.name) {
        const parsed = dayjs(periodo.name, 'DD/MM/YYYY')
        if (parsed.isValid()) {
          dayLimitDate = parsed.toISOString()
        }
      }

      formik.setValues({
        name: periodo?.name,
        duration: periodo?.duration,
        day_limit: dayLimitDate,
        periods_before: periodo?.periods_before.toString(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo])

  return (
    <Grid container>
      <Grid bgcolor="white" item xs={12} borderRadius="6px">
        <Box
          sx={{
            borderRadius: '6px',
            display: 'flex',
            backgroundColor: 'white',
            height: '100%',
            width: '100%',
            paddingRight: '18px',
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Box
              id="periodo-stepper-form"
              component="form"
              autoComplete="off"
              borderRadius="6px"
              sx={{ backgroundColor: 'white', height: '100%' }}
              onSubmit={formik.handleSubmit}
            >
              <Grid container justifyContent="space-between">
                <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                  <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                    <InputLabel sx={{ color: 'black' }} htmlFor="nombre-input">
                      {t('fields.name')}
                    </InputLabel>
                  </Box>
                  <TextField
                    id="nombre-input"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    fullWidth
                    helperText={formik.touched.name && formik.errors.name}
                    name="name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="text"
                    value={formik.values.name}
                    variant="filled"
                    placeholder={t('fields.namePlaceholder')}
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: '6px',
                        '.MuiInputBase-input': {
                          paddingBottom: '16px',
                          paddingTop: '16px',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                  <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                    <InputLabel sx={{ color: 'black' }} htmlFor="duration-input">
                      {t('fields.duration')}
                    </InputLabel>
                  </Box>
                  <TextField
                    id="duration-select"
                    select
                    error={Boolean(formik.touched.duration && formik.errors.duration)}
                    fullWidth
                    helperText={formik.touched.duration && formik.errors.duration}
                    name="duration"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="number"
                    value={formik.values.duration}
                    variant="filled"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: '6px',
                        '.MuiInputBase-input': {
                          paddingBottom: '16px',
                          paddingTop: '16px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                        },
                      },
                    }}
                  >
                    {durationArray.map(duration => (
                      <MenuItem key={duration.id} value={duration.id}>
                        {duration.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between" paddingBottom="26px">
                <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                  <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                    <InputLabel sx={{ color: 'black' }} htmlFor="day-limit-input">
                      {t('fields.dayLimitDate')}
                    </InputLabel>
                  </Box>
                  <DesktopDatePicker
                    inputFormat="DD/MM/YYYY"
                    open={openDatePicker}
                    onOpen={() => setOpenDatePicker(true)}
                    onClose={() => setOpenDatePicker(false)}
                    value={
                      formik.values.day_limit ? dayjs(formik.values.day_limit) : null
                    }
                    minDate={now.startOf('day')}
                    maxDate={
                      formik.values.duration === 1
                        ? todayIsFirstHalf
                          ? now.date(15).startOf('day')
                          : now.endOf('month')
                        : now.endOf('month')
                    }
                    disablePast
                    shouldDisableDate={date => {
                      if (formik.values.duration !== 1) return false
                      if (todayIsFirstHalf) return date.date() > 15
                      return date.date() <= 15
                    }}
                    onChange={newValue => {
                      if (newValue && dayjs(newValue).isValid()) {
                        formik.setFieldValue(
                          'day_limit',
                          dayjs(newValue).startOf('day').toISOString(),
                        )
                      } else {
                        formik.setFieldValue('day_limit', '')
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        id="day-limit-input"
                        name="day_limit"
                        variant="filled"
                        fullWidth
                        error={Boolean(
                          formik.touched.day_limit && formik.errors.day_limit,
                        )}
                        helperText={formik.touched.day_limit && formik.errors.day_limit}
                        onBlur={formik.handleBlur}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          onClick: () => setOpenDatePicker(true),
                          sx: {
                            borderRadius: '6px',
                            cursor: 'pointer',
                            '.MuiInputBase-input': {
                              paddingBottom: '16px',
                              paddingTop: '16px',
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                  <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                    <InputLabel sx={{ color: 'black' }} htmlFor="periods-before-select">
                      {t('fields.advance')}
                    </InputLabel>
                  </Box>
                  <TextField
                    id="periods-before-select"
                    select
                    error={Boolean(
                      formik.touched.periods_before && formik.errors.periods_before,
                    )}
                    fullWidth
                    helperText={
                      formik.touched.periods_before && formik.errors.periods_before
                    }
                    name="periods_before"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.periods_before}
                    variant="filled"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: '6px',
                        '.MuiInputBase-input': {
                          paddingBottom: '16px',
                          paddingTop: '16px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                        },
                      },
                    }}
                  >
                    {periodsBefore.map(period => (
                      <MenuItem key={period.id} value={period.value}>
                        {period.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default PeriodoStepperDetails
